import { Platform } from "~/ui/primitives"
import axios, { AxiosError, AxiosInstance } from "axios"
import BlobCourier from "~/ui/blob-courier"
import DeviceInfo, { getWebBrowserName, getWebDeviceId, getWebLoginDeviceName } from "~/ui/device-info"
import { basename, debug, makeFilters } from "../../utils"
// import { APIError } from "./api-error"
import * as Types from "./api.types"
import { decrypt } from "../../services/crypto"

declare module "axios" {
  export interface AxiosRequestConfig {
    metadata?: { timer: NodeJS.Timeout }
  }
}

const TIMEOUT = 12 * 1000

let deviceHeaderPromise: Promise<{ id: string; name: string }> | null = null
function deviceHeaders(): Promise<{ id: string; name: string }> {
  if (!deviceHeaderPromise) {
    deviceHeaderPromise = Promise.all([
      DeviceInfo.getUniqueId(),
      DeviceInfo.getLoginDeviceName?.() ?? DeviceInfo.getDeviceName(),
    ]).then(([id, name]) => ({
      id: id ?? "",
      name: name ?? "",
    }))
  }
  return deviceHeaderPromise
}

/** XHR setRequestHeader 仅接受 ISO-8859-1，过滤掉超出范围字符 */
function toIso88591HeaderValue(v: unknown): string {
  return String(v ?? "").replace(/[^\x00-\xff]/g, "")
}

/** 系统名 + 版本，如 "iOS 18.2"、"Android 14" */
function deviceOsVersion(): string {
  let family = ""
  let ver = ""
  try {
    family = String(DeviceInfo.getSystemName() ?? "").trim()
    const raw = DeviceInfo.getSystemVersion()
    ver = raw != null && String(raw).length ? String(raw) : ""
  } catch {
    // ignore，下面用 Platform 兜底
  }
  if (!family) {
    family = Platform.OS === "ios" ? "iOS" : Platform.OS === "android" ? "Android" : Platform.OS
  }
  if (!ver) {
    ver = typeof Platform.Version === "number" ? String(Platform.Version) : Platform.Version ?? ""
  }
  return ver ? `${family} ${ver}`.trim() : family
}

const codeMessage = {
  ERR_CANCELED: "请求超时，请检查网路状态。",
  200: "服务器成功返回请求的数据。",
  201: "新建或修改数据成功。",
  202: "一个请求已经进入后台排队（异步任务）。",
  204: "删除数据成功。",
  400: "发出的请求有错误，服务器没有进行新建或修改数据的操作。",
  401: "用户没有权限（令牌、用户名、密码错误）。",
  403: "检测到账号异常，请联系客服",
  404: "发出的请求针对的是不存在的记录，服务器没有进行操作。",
  406: "请求的格式不可得。",
  410: "请求的资源被永久删除，且不会再得到的。",
  422: "Unprocessable Entity",
  429: "请求太频繁了",
  500: "服务器发生错误，请检查服务器。",
  502: "网关错误。",
  503: "服务不可用，服务器暂时过载或维护。",
  504: "网关超时。",
}

export class Client {
  axiosInstance: AxiosInstance

  constructor() {
    this.axiosInstance = axios.create({
      timeout: TIMEOUT,
      headers: {
        Accept: "application/json",
        "RN-Platform-OS": Platform.OS,
      },
    })

    // ---------------------
    // 请求拦截器
    // ---------------------
    this.axiosInstance.interceptors.request.use(async (config: any) => {
      const controller = new AbortController()
      config.signal = controller.signal

      const ms = config.timeout ?? TIMEOUT
      const timer = setTimeout(() => controller.abort(), ms)

      // 用于 response 拦截器清除定时器
      config.metadata = { timer }

      if (config.url) {
        const hasQuery = config.url.includes("?")
        const joiner = hasQuery ? "&" : "?"
        config.url = `${config.url}${joiner}`
        // config.url = `${config.url}`
      }

      try {
        const { id, name } = await deviceHeaders()
        config.headers["X-Device-Id"] = toIso88591HeaderValue(id)
        config.headers["X-Device-Name"] = toIso88591HeaderValue(name)
      } catch {
        config.headers["X-Device-Id"] = toIso88591HeaderValue(getWebDeviceId())
        config.headers["X-Device-Name"] = toIso88591HeaderValue(getWebLoginDeviceName())
      }
      config.headers["X-Device-OS"] = toIso88591HeaderValue(deviceOsVersion())

      debug(`-->[${config.method?.toUpperCase()}] ${config.url}`)
      return config
    })

    // ---------------------
    // 响应拦截器
    // ---------------------
    this.axiosInstance.interceptors.response.use(
      (response) => {
        clearTimeout(response.config.metadata?.timer)
        let _data = response.data
        try {
          _data = {
            ...(response.data?.['x-data'] ? JSON.parse(decrypt(response.data?.['x-data'])) : response.data),
          }
        } catch (error) {
          return Promise.reject(
            new Error('接口数据 x-data 解密或者序列化失败')
          )
        }
        return _data
      },
      (error: AxiosError) => {
        const response = error?.response
        const code = response?.data?.code || error?.code || response?.status
        const errText = response?.data?.message || error?.message || response?.statusText || "网络错误"
        const message = codeMessage[code] || errText

        error.message = message

        debug(`errText: ${errText}, respCode: ${code}, respErr: ${message}`)

        clearTimeout(error.config?.metadata?.timer)

        // throw new APIError(msg, Number(code) || -1)
        return Promise.reject(error);
      },
    )
  }

  // --------------------------
  // Basic HTTP methods
  // --------------------------
  async json<T>(url: string, data: any): Promise<T> {
    return this.axiosInstance.post(url, JSON.stringify(data), {
      headers: { "Content-Type": "application/json" },
    })
  }

  async post<T>(url: string, data: any): Promise<T> {
    return this.axiosInstance.post(url, data)
  }

  async get<T>(url: string): Promise<T> {
    return this.axiosInstance.get(url)
  }

  async getWithAuth<T>(url: string, token: string, params?: any): Promise<T> {
    if (params) url += `?${makeFilters(params)}`
    return this.axiosInstance.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
  }

  async getWithAuthParams<T>(url: string, token: string, params?: any): Promise<T> {
    return this.axiosInstance({
      url,
      method: 'GET',
      params,
      headers: { Authorization: `Bearer ${token}` }
    })
  }

  async postWithAuth<T>(url: string, token: string, data: any): Promise<T> {
    return this.axiosInstance.post(url, data, {
      headers: { Authorization: `Bearer ${token}` },
    })
  }

  async deleteWithAuth<T>(url: string, token: string, data: any): Promise<T> {
    return this.axiosInstance.delete(url, {
      data,
      headers: { Authorization: `Bearer ${token}` },
    })
  }

  async createPay<T>(url: string, token: string, data: any): Promise<T> {
    return this.axiosInstance.post(url, data, {
      headers: {
        "content-type": "multipart/form-data",
        Authorization: `Bearer ${token}`,
      },
    })
  }

  async postFormDataWithAuth<T>(url: string, token: string, data: any): Promise<T> {
    return this.axiosInstance.post(url, data, {
      headers: {
        "content-type": "multipart/form-data",
        Authorization: `Bearer ${token}`,
      },
    })
  }

  // --------------------------
  // Upload & Download (BlobCourier)
  // --------------------------
  async upload(url: string, { path, type, onProgressUpdate }: Types.UploadParams): Promise<Types.UploadResult> {
    return upload({
      url, // 接口地址
      path,
      mimeType: type,
      method: "POST",
      onProgress: ({ written, total }) => {
        onProgressUpdate && onProgressUpdate(written / total)
      },
    })
  }

  async uploadToS3Storage<T>(
    s3url: string,
    formData: any,
    onProgressUpdate,
  ): Promise<T> {
    return this.axiosInstance.post(s3url, formData, {
      timeout: 60000,
      headers: { "content-type": "multipart/form-data" },
      onUploadProgress: onProgressUpdate,
    })
  }

  async download({ url, onProgressUpdate }: Types.DownloadParams) {
    return download({
      url,
      onProgress: ({ written, total }) => {
        onProgressUpdate && onProgressUpdate(written / total)
      },
    })
  }
}

// =======================
// BlobCourier helpers
// =======================
function ensureFileScheme(path: string) {
  if (Platform.OS === "ios") {
    if (path.startsWith("assets-library://")) return path
    else if (!path.startsWith("file://")) return "file://" + path
  }
  return path
}

export async function upload({
  url,
  path,
  mimeType,
  onProgress,
  method = "POST",
  fieldName = "file"
}) {
  const absoluteFilePath = ensureFileScheme(path)

  const result = await BlobCourier.uploadBlob({
    absoluteFilePath,
    url,
    mimeType: mimeType,
    method,
    multipartName: fieldName,
    returnResponse: true,
    progressIntervalMilliseconds: 200,
    onProgress,
  })

  const data = result?.response?.data
  const parsed = typeof data === "string" ? JSON.parse(data) : data
  if (parsed?.["x-data"]) {
    return JSON.parse(decrypt(parsed["x-data"]))
  }
  return parsed
}

export async function download({ url, onProgress, method = "GET" }) {
  const result = await BlobCourier.fetchBlob({
    method,
    url,
    filename: basename(url),
    mimeType: "",
    onProgress,
  })
  return ensureFileScheme(result.data.absoluteFilePath)
}
